import React, { useEffect, useMemo, useState } from 'react';
import { createAuthDependencies } from '@/app/auth/createAuthDependencies';
import { useAppTranslation } from '@/i18n/useAppTranslation';
import { LocaleSelect } from '@/presentation/dashboard/LocaleSelect';
import { formatApiErrorMessage } from '@/utils/formatApiErrorMessage';

const REG_SESSION_KEY = 'eduquiz_register_v1';

export default function RegisterPage({ onLogin, onRegistered }) {
  const { t, locale, setLocale } = useAppTranslation();
  const deps = useMemo(() => createAuthDependencies(), []);
  const [role, setRole] = useState('teacher');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSendSucceeded, setOtpSendSucceeded] = useState(false);
  const [registrationToken, setRegistrationToken] = useState('');
  const [otpMessage, setOtpMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hydrated, setHydrated] = useState(false);

  const emailVerified = Boolean(registrationToken);
  const canVerifyEmail = otpSendSucceeded && /^\d{6}$/.test(otpCode.trim());

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(REG_SESSION_KEY);
      if (!raw) {
        setHydrated(true);
        return;
      }
      const d = JSON.parse(raw);
      if (typeof d.name === 'string') setName(d.name);
      if (typeof d.email === 'string') setEmail(d.email);
      if (typeof d.role === 'string') setRole(d.role);
      if (typeof d.registrationToken === 'string' && d.registrationToken) {
        setRegistrationToken(d.registrationToken);
        setOtpSendSucceeded(false);
        setOtpCode('');
      }
    } catch {
      /* ignore */
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      if (!name.trim() && !email.trim() && !registrationToken) {
        sessionStorage.removeItem(REG_SESSION_KEY);
        return;
      }
      sessionStorage.setItem(
        REG_SESSION_KEY,
        JSON.stringify({ name, email, role, registrationToken })
      );
    } catch {
      /* ignore */
    }
  }, [hydrated, name, email, role, registrationToken]);

  const clearSession = () => {
    try {
      sessionStorage.removeItem(REG_SESSION_KEY);
    } catch {
      /* ignore */
    }
  };

  const resetAll = () => {
    setPassword('');
    setPasswordConfirm('');
    setOtpCode('');
    setOtpSendSucceeded(false);
    setRegistrationToken('');
    setOtpMessage('');
    setError('');
    clearSession();
  };

  const sendOtp = async () => {
    setError('');
    setOtpMessage('');
    if (!name?.trim() || !email?.trim()) {
      setError(t('auth.register.missingNameEmail'));
      return;
    }
    setLoading(true);
    try {
      await deps.requestRegisterCode.execute({ name, email, role });
      setOtpSendSucceeded(true);
      setOtpMessage(t('auth.register.otpSent', { email: email.trim() }));
    } catch (e) {
      setOtpSendSucceeded(false);
      setError(formatApiErrorMessage(e?.message, t) || t('auth.register.otpSendFailed'));
    } finally {
      setLoading(false);
    }
  };

  const verifyEmail = async () => {
    setError('');
    if (!canVerifyEmail) return;
    setLoading(true);
    try {
      const data = await deps.verifyRegisterOtp.execute({
        name: name.trim(),
        email: email.trim(),
        role,
        code: otpCode.trim(),
      });
      const token = data?.registration_token;
      if (!token) {
        setError(t('auth.register.serverInvalid'));
        return;
      }
      setRegistrationToken(token);
      setOtpMessage('');
      setOtpCode('');
      setOtpSendSucceeded(false);
      setPassword('');
      setPasswordConfirm('');
    } catch (e) {
      setError(formatApiErrorMessage(e?.message, t) || t('auth.register.otpInvalid'));
    } finally {
      setLoading(false);
    }
  };

  const completeRegistration = async (event) => {
    event.preventDefault();
    setError('');
    if (!registrationToken) {
      setError(t('auth.register.needVerifyFirst'));
      return;
    }
    if (!password || password.length < 8) {
      setError(t('auth.register.passwordMin'));
      return;
    }
    if (password !== passwordConfirm) {
      setError(t('auth.register.passwordMismatch'));
      return;
    }
    setLoading(true);
    try {
      const result = await deps.completeRegister.execute({
        email: email.trim(),
        password,
        registration_token: registrationToken,
      });
      clearSession();
      onRegistered?.(result, role);
    } catch (e) {
      const msg = formatApiErrorMessage(e?.message, t) || t('auth.register.failed');
      setError(msg);
      if (/401|expired|invalid|unauthorized|hết hạn|không hợp lệ|phiên/i.test(msg)) {
        setRegistrationToken('');
        setOtpSendSucceeded(false);
        setOtpCode('');
        setPassword('');
        setPasswordConfirm('');
        setOtpMessage(t('auth.register.sessionExpired'));
      }
    } finally {
      setLoading(false);
    }
  };

  const onNameEmailChange = (setter) => (e) => {
    setter(e.target.value);
    if (!emailVerified) {
      setOtpSendSucceeded(false);
      setOtpCode('');
    }
  };

  return (
    <main className="min-h-screen bg-sky-50 p-4 md:p-8">
      <div className="pointer-events-none fixed right-4 top-4 z-50 pointer-events-auto">
        <LocaleSelect value={locale} onChange={setLocale} ariaLabel={t('auth.langSwitch')} />
      </div>
      <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-8 lg:grid-cols-12">
        <section className="relative hidden overflow-hidden rounded-3xl bg-emerald-300 p-12 lg:col-span-5 lg:flex lg:flex-col lg:justify-between">
          <div>
            <h1 className="font-headline text-5xl font-extrabold text-emerald-950">{t('auth.register.heroTitle')}</h1>
            <p className="mt-6 max-w-md text-emerald-900">{t('auth.register.heroSubtitle')}</p>
          </div>
          <div className="rounded-2xl border border-white/40 bg-white/40 p-5">
            <p className="text-sm font-bold text-slate-900">{t('auth.register.heroCardTitle')}</p>
            <p className="text-xs text-slate-700">{t('auth.register.heroCardSubtitle')}</p>
          </div>
        </section>

        <section className="rounded-3xl bg-white p-8 shadow-sm lg:col-span-7 md:p-12">
          <div className="mx-auto w-full max-w-md">
            <h2 className="font-headline text-3xl font-bold text-slate-900">{t('auth.register.title')}</h2>
            <p className="mt-2 text-slate-600">
              {!emailVerified
                ? t('auth.register.step1Help')
                : t('auth.register.step2Help')}
            </p>

            <div className="mt-6 flex gap-2" aria-hidden="true">
              <div className={`h-1 flex-1 rounded-full ${!emailVerified ? 'bg-emerald-600' : 'bg-emerald-200'}`} />
              <div className={`h-1 flex-1 rounded-full ${emailVerified ? 'bg-emerald-600' : 'bg-slate-200'}`} />
            </div>
            <p className="mt-2 text-center text-xs font-semibold text-slate-500">
              {!emailVerified ? t('auth.register.step1Indicator') : t('auth.register.step2Indicator')}
            </p>

            <div className="mt-8 space-y-6">
              <div>
                <label className="mb-3 block text-xs font-semibold uppercase tracking-wider text-slate-500">{t('auth.register.roleLabel')}</label>
                <div className={`grid grid-cols-3 gap-3 ${emailVerified ? 'pointer-events-none opacity-70' : ''}`}>
                  {[
                    { value: 'teacher', label: t('auth.register.roleTeacher'), icon: 'school' },
                    { value: 'student', label: t('auth.register.roleStudent'), icon: 'person_play' },
                    { value: 'parent', label: t('auth.register.roleParent'), icon: 'family_restroom' },
                  ].map((item) => (
                    <button
                      key={item.value}
                      type="button"
                      disabled={emailVerified}
                      onClick={() => setRole(item.value)}
                      className={`rounded-xl border-2 p-3 text-center transition ${
                        role === item.value ? 'border-emerald-600 bg-emerald-50' : 'border-transparent bg-slate-50'
                      }`}
                    >
                      <span className="material-symbols-outlined block text-2xl text-emerald-700">{item.icon}</span>
                      <span className="text-xs font-bold">{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <label className="block">
                <span className="mb-2 block text-sm font-medium text-slate-700">{t('auth.register.nameLabel')}</span>
                <input
                  value={name}
                  onChange={onNameEmailChange(setName)}
                  readOnly={emailVerified}
                  className={`w-full rounded-xl border-0 px-4 py-3.5 focus:ring-2 focus:ring-emerald-300 ${
                    emailVerified ? 'cursor-not-allowed bg-slate-100 text-slate-600' : 'bg-sky-50'
                  }`}
                  placeholder={t('auth.register.namePlaceholder')}
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-medium text-slate-700">{t('auth.register.emailLabel')}</span>
                <input
                  value={email}
                  type="email"
                  onChange={onNameEmailChange(setEmail)}
                  readOnly={emailVerified}
                  className={`w-full rounded-xl border-0 px-4 py-3.5 focus:ring-2 focus:ring-emerald-300 ${
                    emailVerified ? 'cursor-not-allowed bg-slate-100 text-slate-600' : 'bg-sky-50'
                  }`}
                  placeholder={t('auth.login.emailPlaceholder')}
                />
              </label>

              {!emailVerified ? (
                <>
                  <div>
                    <span className="mb-2 block text-sm font-medium text-slate-700">{t('auth.register.otpLabel')}</span>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
                      <input
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="min-w-0 flex-1 rounded-xl border-0 bg-sky-50 px-4 py-3.5 text-center text-xl font-mono tracking-[0.35em] focus:ring-2 focus:ring-emerald-300"
                        placeholder="••••••"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                      />
                      <button
                        type="button"
                        disabled={loading}
                        onClick={sendOtp}
                        className="shrink-0 rounded-xl border-2 border-emerald-600 bg-emerald-50 px-5 py-3.5 text-sm font-bold text-emerald-800 transition hover:bg-emerald-100 disabled:opacity-50"
                      >
                        {loading ? t('auth.register.ellipsis') : t('auth.register.sendOtp')}
                      </button>
                    </div>
                    <p className="mt-1.5 text-xs text-slate-500">
                      {t('auth.register.otpHint')}
                    </p>
                  </div>

                  {otpMessage ? (
                    <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{otpMessage}</p>
                  ) : null}
                  {error ? <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p> : null}

                  <button
                    type="button"
                    disabled={!canVerifyEmail || loading}
                    onClick={verifyEmail}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-700 px-6 py-4 text-lg font-bold text-white shadow-lg shadow-emerald-200 transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {loading ? t('auth.register.verifying') : t('auth.register.verifyEmail')}
                    <span className="material-symbols-outlined text-xl">mark_email_read</span>
                  </button>
                </>
              ) : (
                <>
                  <div className="rounded-xl border border-emerald-100 bg-emerald-50/80 px-4 py-3 text-sm text-emerald-900">
                    {t('auth.register.verifiedLineA')}{' '}
                    <span className="font-semibold">{email.trim()}</span>. {t('auth.register.verifiedLineB')}
                  </div>

                  <form onSubmit={completeRegistration} className="space-y-5">
                    <label className="block">
                      <span className="mb-2 block text-sm font-medium text-slate-700">{t('auth.register.passwordLabel')}</span>
                      <input
                        value={password}
                        type="password"
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full rounded-xl border-0 bg-sky-50 px-4 py-3.5 focus:ring-2 focus:ring-emerald-300"
                        placeholder={t('auth.register.passwordPlaceholder')}
                        autoComplete="new-password"
                        autoFocus
                      />
                    </label>
                    <label className="block">
                      <span className="mb-2 block text-sm font-medium text-slate-700">{t('auth.register.passwordConfirmLabel')}</span>
                      <input
                        value={passwordConfirm}
                        type="password"
                        onChange={(e) => setPasswordConfirm(e.target.value)}
                        className="w-full rounded-xl border-0 bg-sky-50 px-4 py-3.5 focus:ring-2 focus:ring-emerald-300"
                        placeholder={t('auth.register.passwordConfirmPlaceholder')}
                        autoComplete="new-password"
                      />
                    </label>
                    {error ? <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p> : null}
                    <button
                      disabled={loading}
                      type="submit"
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-700 px-6 py-4 text-lg font-bold text-white shadow-lg shadow-emerald-200 transition hover:bg-emerald-800 disabled:opacity-50"
                    >
                      {loading ? t('auth.register.creating') : t('auth.register.complete')}
                      <span className="material-symbols-outlined">check</span>
                    </button>
                  </form>
                </>
              )}

              <div className="flex flex-col gap-2 border-t border-slate-100 pt-6 sm:flex-row sm:justify-between">
                <button
                  type="button"
                  disabled={loading}
                  onClick={resetAll}
                  className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                >
                  {t('auth.register.resetForm')}
                </button>
                <button type="button" onClick={onLogin} className="text-sm font-semibold text-emerald-700 hover:underline">
                  {t('auth.register.haveAccount')}
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
